import {FC, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useAddBranchToStationMutation} from 'store/slices/stations';
import {ISnackbarState} from 'store/models/common';
import {useUpdateEffect} from 'hooks';
import {ErrorMessage} from '@hookform/error-message';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Modal,
  Paper,
  Snackbar,
  TextField,
} from '@mui/material';
import ButtonWithProgress from 'components/ButtonWithProgress/ButtonWithProgress';
import {CloseIcon, SelectPopupIcon, SuccessNotification} from 'assets/icons';
import {searchAndSelect, searchAndSelectError} from 'styles/MUIStyles/common';
import {cancelButton, createButtonModal} from 'styles/MUIStyles/buttons';
import {
  snackbarAlertContent,
  snackbarSuccessAlert,
} from 'styles/MUIStyles/snackbars';
import {modal} from 'styles/MUIStyles/modal';
import styles from './AssignStationModal.module.scss';
import {IGetBranchesList} from 'store/models/branches';
import {useLazyGetUnassignedStationsByBranchQuery} from 'store/slices/branches';
import {getServerErrors} from 'core/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clickedBranch: IGetBranchesList | null;
  isStopPropogation?: boolean;
}

interface IFormData {
  station: {
    id: string;
    name: string;
    posChassicNumber: string;
  };
}

const AssignStationModal: FC<Props> = (props) => {
  const {
    handleSubmit,
    reset,
    clearErrors,
    setError,
    control,
    formState: {errors},
  } = useForm<IFormData>({
    defaultValues: {
      station: {
        id: '',
        name: '',
        posChassicNumber: '',
      },
    },
  });

  const [getStationsList, {data: stationsList, isFetching}] =
    useLazyGetUnassignedStationsByBranchQuery();

  const [addBranchToStation, {isLoading, isSuccess, error}] =
    useAddBranchToStationMutation();

  const [stationName, setStationName] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<ISnackbarState>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });

  const {vertical, horizontal, open} = snackbar;

  const onSubmit = async (formData: IFormData) => {
    if (props.clickedBranch) {
      const response = await addBranchToStation({
        stationId: formData.station.id,
        branchId: props.clickedBranch.id,
      });

      if (response && !('error' in response)) {
        setStationName(formData.station.name);
      }
    }
  };

  const clearDataAndErrors = () => {
    reset();
    clearErrors();
    props.onClose();
  };

  const handleSnackbarClose = () => {
    setStationName(null);
    setSnackbar({...snackbar, open: false});
  };

  useUpdateEffect(() => {
    if (props.isOpen && props.clickedBranch) {
      getStationsList(props.clickedBranch.id);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (error && 'data' in error && 'status' in error) {
      if (error.status === 400 || error.status === 404) {
        const errors = getServerErrors(error.data);

        if (errors?.branchId?.[0]) {
          setError('station', {type: 'custom', message: errors.branchId[0]});
        }

        if (errors?.stationId?.[0]) {
          setError('station', {type: 'custom', message: errors.stationId[0]});
        }
      }
    }
  }, [error, setError]);

  useUpdateEffect(() => {
    if (isSuccess) {
      setSnackbar({vertical: 'top', horizontal: 'center', open: true});
      reset();
      props.onClose();
    }
  }, [isSuccess]);

  return (
    <>
      <Modal open={props.isOpen} onClose={clearDataAndErrors}>
        <Box sx={{...modal, width: '35%'}}>
          <form
            onSubmit={(event) => {
              if (props.isStopPropogation) {
                event.stopPropagation();
                handleSubmit(onSubmit)(event);
              } else {
                handleSubmit(onSubmit)(event);
              }
            }}
          >
            <div className={styles.modalTop}>
              <h2 className={styles.stationsAddBranchTitle}>Assign station</h2>
              <CloseIcon onClick={clearDataAndErrors} />
            </div>
            <Controller
              name={'station'}
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.name && !value?.id) {
                    return 'This field cannot be empty';
                  }
                },
              }}
              render={({field}) => (
                <Autocomplete
                  {...field}
                  options={stationsList || []}
                  loading={isFetching}
                  popupIcon={<SelectPopupIcon />}
                  PaperComponent={(props) => (
                    <Paper
                      sx={{
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-card)',
                        marginTop: '8px',
                        cursor: 'pointer',
                      }}
                      {...props}
                    />
                  )}
                  sx={{width: '100%'}}
                  ListboxProps={{
                    style: {maxHeight: '200px'},
                    className: 'autoCompleteListBox',
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value.name === '' ? true : option.id === value.id
                  }
                  getOptionLabel={(option) => (option?.name ? option.name : '')}
                  onChange={(_, value) => {
                    if (value) {
                      field.onChange(value);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  renderOption={(props: any, option) => (
                    <span {...props} className={styles.dropDownItem}>
                      {option?.name} / {option?.posChassicNumber}
                    </span>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select station"
                      sx={
                        errors?.station
                          ? searchAndSelectError
                          : {...searchAndSelect, marginBottom: '16px'}
                      }
                    />
                  )}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name={'station'}
              render={({message}) => (
                <span className={styles.inputErrorMessage}>{message}</span>
              )}
            />
            <Divider
              sx={{backgroundColor: 'var(--border)', margin: '18px 0 14px 0'}}
            />
            <div className={styles.buttonsContainer}>
              <Button sx={cancelButton} onClick={clearDataAndErrors}>
                Cancel
              </Button>
              <ButtonWithProgress
                type="submit"
                sx={{...createButtonModal}}
                loading={isLoading}
                disabled={isLoading}
              >
                Add branch
              </ButtonWithProgress>
            </div>
          </form>
        </Box>
      </Modal>
      {stationName && props.clickedBranch ? (
        <Snackbar
          anchorOrigin={{vertical, horizontal}}
          autoHideDuration={2500}
          open={open}
          onClose={handleSnackbarClose}
          key={vertical + horizontal}
        >
          <Alert icon={false} sx={snackbarSuccessAlert}>
            <Box sx={snackbarAlertContent}>
              <SuccessNotification />
              Station {`"${stationName}"`} successfully assigned to branch{' '}
              {`"${props.clickedBranch.name}"`}
              <span onClick={handleSnackbarClose}>
                <CloseIcon />
              </span>
            </Box>
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
};

export default AssignStationModal;
